import { useCalendarContext } from "PFApp/booking/parts/overview/context/calendar_context";
import { getCategoryBorderColor } from "PFApp/components/pills/booking_pill";
import React, { useMemo } from "react";

import { useBookingBarContext } from "../booking_bar_provider";
import { ItemContent } from "./item_content";
import css from "./item_groups.module.scss";

const BORDER_WIDTH = 2.5; //pill border 2px + pill container padding 0.5px
const MIN_WIDTH = 40;

type ItemGroupsProps = {
  dateGroups: { date: string; hidden: boolean }[][];
};

type Group = {
  dates: ItemGroupsProps["dateGroups"][0];
  width: number;
  visible: boolean;
  boxShadow: string | undefined;
};

export const ItemGroups = ({ dateGroups }: ItemGroupsProps) => {
  const { category } = useBookingBarContext();
  const { dayWidth } = useCalendarContext();

  const groups = useMemo<Group[]>(
    () =>
      dateGroups
        .map((dates, groupIndex) => {
          const isFirst = groupIndex === 0;
          const isLast = groupIndex === dateGroups.length - 1;
          const visibleDates = dates.filter(({ hidden }) => !hidden);
          const baseWidth = dayWidth * visibleDates.length;
          const width = Math.max(
            0,
            baseWidth - (isFirst ? BORDER_WIDTH : 0) - (isLast ? BORDER_WIDTH : 0) // in case it's a single item, it subtracts 2*BORDER_WIDTH
          );
          const visible = baseWidth > MIN_WIDTH; // comparing baseWidth so that all parts of the pill (including first and last which are smaller) are visible or not
          return { dates, width, visible };
        })
        .filter(({ width }) => !!width)
        .map((group, groupIndex) => {
          const isFirst = groupIndex === 0;
          const boxShadow = !isFirst ? `inset 1px 0 0 ${getCategoryBorderColor(category)}` : undefined;
          return {
            ...group,
            boxShadow
          };
        }),
    [dateGroups, category, dayWidth]
  );

  return (
    <div className={css.root}>
      {groups.map(({ dates, width, boxShadow, visible }) => (
        <div key={dates[0]?.date} className={css.item} style={{ width, boxShadow }}>
          {visible && <ItemContent dates={dates} />}
        </div>
      ))}
    </div>
  );
};
